import React, { useMemo } from "react";
import { Popover, Tabs } from "antd";
import classes from "./result-tabs.module.scss";
import ResultBox from "./result-box";
import { useSelector } from "react-redux";
import { selectProjectObject } from "../../../../reducers/project/selectors";
import { OBJECT_TYPES } from "../../../../constants/project";
import {dissoc, isEmpty, isNil, pathSatisfies, pipe, pluck, propOr, when} from "ramda";
import { selectFileName } from "../../../../reducers/pdf-viewer/selectors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import IntegromatResultBox from "./integromat-result-box";
import ZapierResultBox from "./zapier-result-box";

import {getAnnotationPureData, getFieldPureData, getImagePureData} from "../../../../helpers/objects";

const { TabPane } = Tabs;

const ResultTabs = () => {
  const projects = useSelector(selectProjectObject);
  const pdfFileName = useSelector(selectFileName);


  const jsonData = useMemo(() => {
    const annotationProjects = projects
      .filter((item) => item.objectType === OBJECT_TYPES.ANNOTATION)
      .map((item) => {
        return getAnnotationPureData(item);
      });

    const imageProjects = projects
      .filter((item) => item.objectType === OBJECT_TYPES.IMAGE)
      .map((item) => {
        return getImagePureData(item);
      });

    const fieldProjects = projects
      .filter((item) => item.objectType === OBJECT_TYPES.DETECTED_FIELD)
      .map((item) => {
        return getFieldPureData(item);
      });


    const data = {
      url: (isNil(pdfFileName) ? "" : `INSERT_URL_TO_INPUT_FILE_${pdfFileName}`),
      async: false,
      encrypt: false,
      inline: true,
      annotations: [...annotationProjects],
      images: imageProjects,
      fields: fieldProjects,
    };

    return imageProjects.length ||
      annotationProjects.length ||
      fieldProjects.length
      ? JSON.stringify(data, null, 4).trim()
      : "";
  }, [projects, pdfFileName]);

  const cUrlData = useMemo(() => {

    var data = jsonData;

    return "curl --location 'https://api.pdf.co/v1/pdf/edit/add' \\\ \r\n" +
    "--header 'Content-Type: application/json' \\\ \r\n" +
    "--header 'x-api-key: INSERT_YOUR_API_KEY_HERE' \\\ \r\n" +
    "--data '{" + jsonData + "}'";
    
  }, [projects, pdfFileName]);

  return (
    <div className={classes.ResultsWrap}>
      <Tabs type="card" defaultActiveKey="zapier" className={classes.TabsWrap}>
        <TabPane tab="Zapier" key="zapier">
          <div className={classes.TabContent}>
            <div>
              <div className={classes.Title}>
                <div>Zapier config for <a href="https://zapier.com/apps/pdfco/integrations" target="_blank">PDF.co PDF Filler plugin for Zapier</a>:</div>
                <div>
                  <Popover
                    content={
                      <div className={classes.Description}>
                        Use the lines below with Zapier and other plugins to add
                        text or images. For images you need to set url or
                        datauri to your image instead of the comment. You can
                        also fill PDF forms, here is{" "}
                        <a
                          href="https://pdf.co/how-to-fill-pdf-forms-using-zapier"
                          target="_blank"
                          className={"text-warning"}
                        >
                          API docs
                        </a>{" "}
                        for more info. You can also{" "}
                        <a
                          href="https://apidocs.pdf.co/index#05-pdf-fill-pdf-forms"
                          target="_blank"
                          className={"text-warning"}
                        >
                          the tutorial
                        </a>{" "}
                        You can also convert signature into image{" "}
                        <a
                          href="https://app.pdf.co/make-signature"
                          target="_blank"
                          className={"text-warning"}
                        >
                          here
                        </a>
                        . <br />
                        For more info on simplified format for adding text,
                        images and filling pdf forms{" "}
                        <a
                          href="https://apidocs.pdf.co/index#2-pdfeditadd-with-simplified-parameters"
                          target="_blank"
                          className={"text-warning"}
                        >
                          check here
                        </a>
                      </div>
                    }
                    title={null}
                    trigger="hover"
                  >
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className={classes.QuestionIcon}
                    />
                  </Popover>
                </div>
              </div>
            </div>
            <ZapierResultBox data={projects} />
          </div>
        </TabPane>

        <TabPane tab="Make" key="integromat">
          <div className={classes.TabContent}>
            <div className={classes.Title}>
              <div>Make (Integromat) config for <a href="https://www.make.com/en/integrations/pdf-co" target="_blank">Fill a PDF Form</a> plugin: </div></div>
            <IntegromatResultBox data={projects} />
          </div>
        </TabPane>

        <TabPane tab="JSON" key="json">
          <div className={classes.TabContent}>
            <div>
              <div className={classes.Title}>
                <div>JSON for API call of <a href="https://apidocs.pdf.co/05-pdf-fill-pdf-forms" target="_blank">pdf/edit/add</a> endpoint:</div>

                <div>
                  <Popover
                    content={
                      <div className={classes.Description}>
                        Use this JSON code for <code>https://api.pdf.co/v1/pdf/edit/add</code> API endpoint. See{" "}
                        <a
                          href="https://apidocs.pdf.co/04-pdf-add-text-signatures-and-images-to-pdf"
                          target="_blank"
                          className={"text-warning"}
                        >
                          API docs
                        </a>{" "}
                        for more info. You can also{" "}
                        <a
                          href="https://apidocs.pdf.co/05-pdf-fill-pdf-forms"
                          target="_blank"
                          className={"text-warning"}
                        >
                          fill out PDF forms
                        </a>
                      </div>
                    }
                    title={null}
                    trigger="hover"
                  >
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className={classes.QuestionIcon}
                    />
                  </Popover>
                </div>
              </div>
            </div>

            <ResultBox data={jsonData} showCopyAll={true} />
          </div>
        </TabPane>

        <TabPane tab="cURL" key="curl">
          <div className={classes.TabContent}>
            <div>
              <div className={classes.Title}>
                <div><code>cURL</code> call of <a href="https://apidocs.pdf.co/05-pdf-fill-pdf-forms" target="_blank">pdf/edit/add</a> endpoint:</div>

                <div>
                  <Popover
                    content={
                      <div className={classes.Description}>
                        Use this cURL code for running <code>https://api.pdf.co/v1/pdf/edit/add</code> from command line. See{" "}
                        <a
                          href="https://apidocs.pdf.co/04-pdf-add-text-signatures-and-images-to-pdf"
                          target="_blank"
                          className={"text-warning"}
                        >
                          API docs
                        </a>{" "}
                        for more info. You can also{" "}
                        <a
                          href="https://apidocs.pdf.co/05-pdf-fill-pdf-forms"
                          target="_blank"
                          className={"text-warning"}
                        >
                          fill out PDF forms
                        </a>
                      </div>
                    }
                    title={null}
                    trigger="hover"
                  >
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      className={classes.QuestionIcon}
                    />
                  </Popover>
                </div>
              </div>
            </div>

            <ResultBox data={cUrlData} showCopyAll={true} />
          </div>
        </TabPane>

      </Tabs>
    </div>
  );
};

export default ResultTabs;
